import React from "react";
import { RiAlertLine, RiCheckboxCircleLine } from "@remixicon/react";
import styles from "./index.style.module.css";

export type HealthStatus =
  | "Healthy"
  | "Stable"
  | "Critical"
  | "Loading"
  | "Error";

/** @deprecated Use 'Stable' instead of 'Fair' */
type LegacyFair = "Fair";
/** @deprecated Use 'Critical' instead of 'Risk' */
type LegacyRisk = "Risk";

/** Supports legacy status values for backwards compatibility */
export type HealthStatusWithLegacy = HealthStatus | LegacyFair | LegacyRisk;

export interface StatusWithBarProps {
  /**
   * Health status value
   * Supports: 'Healthy', 'Stable', 'Critical', 'Loading', 'Error'
   * Legacy values 'Fair' and 'Risk' are mapped to 'Stable' and 'Critical'
   */
  status: HealthStatusWithLegacy;
  /**
   * Number of filled segments (0-10)
   */
  filledSegments: number;
  /**
   * Total number of segments
   */
  totalSegments?: number;
  /**
   * Whether to hide the status label
   */
  hideLabel?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/** Maps legacy status values to current values */
const normalizeStatus = (status: HealthStatusWithLegacy): HealthStatus => {
  if (status === "Fair") return "Stable";
  if (status === "Risk") return "Critical";
  return status;
};

// Icon components for each status - matching Figma design

const LoadingSpinner = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.spinner}
  >
    {/* Bottom-left diagonal bar */}
    <path
      d="M3.16455 19.6365C3.16455 19.9547 3.29093 20.2599 3.51589 20.4849C3.62734 20.5965 3.75969 20.685 3.90537 20.7454C4.05104 20.8058 4.2072 20.8369 4.36489 20.8369C4.52259 20.8369 4.67875 20.8058 4.82442 20.7454C4.9701 20.685 5.10245 20.5965 5.21389 20.4849L7.75789 17.9397C7.87251 17.829 7.96393 17.6966 8.02682 17.5502C8.08971 17.4038 8.12281 17.2463 8.12419 17.087C8.12558 16.9277 8.09522 16.7697 8.03488 16.6222C7.97454 16.4747 7.88544 16.3407 7.77277 16.2281C7.6601 16.1154 7.52612 16.0263 7.37864 15.9659C7.23116 15.9056 7.07315 15.8752 6.91381 15.8766C6.75448 15.878 6.59701 15.9111 6.45061 15.974C6.3042 16.0369 6.17179 16.1283 6.06109 16.2429L3.51589 18.7881C3.29093 19.0132 3.16455 19.3183 3.16455 19.6365Z"
      fill="#0000C9"
    />
    {/* Left horizontal bar */}
    <path
      d="M5.64853 12.8486C5.87357 12.6235 6 12.3183 6 12C6 11.6818 5.87357 11.3766 5.64853 11.1515C5.42348 10.9265 5.11826 10.8 4.8 10.8H1.2C0.88174 10.8 0.576515 10.9265 0.351472 11.1515C0.126428 11.3766 0 11.6818 0 12C0 12.3183 0.126428 12.6235 0.351472 12.8486C0.576515 13.0736 0.88174 13.2 1.2 13.2H4.8C5.11826 13.2 5.42348 13.0736 5.64853 12.8486Z"
      fill="#0000C9"
    />
    {/* Top-left diagonal bar */}
    <path
      d="M6.90831 8.10779C7.2265 8.10779 7.53167 7.98142 7.75671 7.75645C7.98167 7.53142 8.10805 7.22625 8.10805 6.90805C8.10805 6.58985 7.98167 6.28468 7.75671 6.05965L5.21271 3.51445C4.98754 3.28944 4.68221 3.1631 4.36388 3.16321C4.04556 3.16332 3.74032 3.28988 3.51531 3.51505C3.2903 3.74022 3.16395 4.04555 3.16406 4.36388C3.16418 4.6822 3.29074 4.98744 3.51591 5.21245L6.05991 7.75645C6.28494 7.98142 6.59011 8.10779 6.90831 8.10779Z"
      fill="#0000C9"
    />
    {/* Top vertical bar */}
    <path
      d="M12.8486 0.351472C12.6235 0.126428 12.3183 0 12 0C11.6818 0 11.3766 0.126428 11.1515 0.351472C10.9265 0.576515 10.8 0.88174 10.8 1.2V4.8C10.8 5.11826 10.9265 5.42348 11.1515 5.64853C11.3766 5.87357 11.6818 6 12 6C12.3183 6 12.6235 5.87357 12.8486 5.64853C13.0736 5.42348 13.2 5.11826 13.2 4.8V1.2C13.2 0.88174 13.0736 0.576515 12.8486 0.351472Z"
      fill="#0000C9"
    />
    {/* Top-right diagonal bar */}
    <path
      d="M15.892 6.90865C15.892 7.22685 16.0183 7.53202 16.2433 7.75705C16.4683 7.98202 16.7735 8.10839 17.0917 8.10839C17.4099 8.10839 17.7151 7.98202 17.9401 7.75705L20.4853 5.21185C20.7103 4.98668 20.8367 4.68135 20.8366 4.36303C20.8364 4.0447 20.7099 3.73946 20.4847 3.51445C20.2595 3.28944 19.9542 3.1631 19.6359 3.16321C19.3176 3.16332 19.0123 3.28988 18.7873 3.51505L16.2433 6.06025C16.0183 6.28528 15.892 6.59045 15.892 6.90865Z"
      fill="#0000C9"
    />
    {/* Right horizontal bar */}
    <path
      d="M23.6485 12.8486C23.8736 12.6235 24 12.3183 24 12C24 11.6818 23.8736 11.3766 23.6485 11.1515C23.4235 10.9265 23.1183 10.8 22.8 10.8H19.2C18.8817 10.8 18.5765 10.9265 18.3515 11.1515C18.1264 11.3766 18 11.6818 18 12C18 12.3183 18.1264 12.6235 18.3515 12.8486C18.5765 13.0736 18.8817 13.2 19.2 13.2H22.8C23.1183 13.2 23.4235 13.0736 23.6485 12.8486Z"
      fill="#0000C9"
    />
    {/* Bottom-right diagonal bar */}
    <path
      d="M19.6369 20.8367C19.9551 20.8367 20.2603 20.7103 20.4853 20.4853C20.5969 20.3739 20.6854 20.2416 20.7458 20.0959C20.8062 19.9502 20.8373 19.794 20.8373 19.6363C20.8373 19.4786 20.8062 19.3225 20.7458 19.1768C20.6854 19.0311 20.5969 18.8988 20.4853 18.7873L17.9401 16.2433C17.7138 16.0248 17.4107 15.9038 17.0961 15.9065C16.7814 15.9093 16.4805 16.0355 16.258 16.258C16.0355 16.4805 15.9093 16.7814 15.9065 17.0961C15.9038 17.4107 16.0248 17.7138 16.2433 17.9401L18.7885 20.4853C19.0136 20.7103 19.3188 20.8367 19.6369 20.8367Z"
      fill="#0000C9"
    />
    {/* Bottom vertical bar */}
    <path
      d="M12.8486 18.3515C12.6235 18.1264 12.3183 18 12 18C11.6818 18 11.3766 18.1264 11.1515 18.3515C10.9265 18.5765 10.8 18.8817 10.8 19.2V22.8C10.8 23.1183 10.9265 23.4235 11.1515 23.6485C11.3766 23.8736 11.6818 24 12 24C12.3183 24 12.6235 23.8736 12.8486 23.6485C13.0736 23.4235 13.2 23.1183 13.2 22.8V19.2C13.2 18.8817 13.0736 18.5765 12.8486 18.3515Z"
      fill="#0000C9"
    />
  </svg>
);

const statusConfig: Record<
  HealthStatus,
  { color: string; icon: React.ReactNode; label: string }
> = {
  Healthy: {
    color: "#008253", // icon-success
    icon: <RiCheckboxCircleLine size={16} color="#008253" />,
    label: "Healthy",
  },
  Stable: {
    color: "#ff7900", // icon-warning
    icon: <RiAlertLine size={16} color="#ff7900" />,
    label: "Stable",
  },
  Critical: {
    color: "#a20f2c", // icon-critical
    icon: <RiAlertLine size={16} color="#a20f2c" />,
    label: "Critical",
  },
  Loading: {
    color: "#0000C9", // icon-blue
    icon: <LoadingSpinner />,
    label: "Loading in few seconds...",
  },
  Error: {
    color: "#a20f2c", // icon-critical
    icon: <RiAlertLine size={16} color="#a20f2c" />,
    label: "Error Occurred",
  },
};

/**
 * StatusWithBar displays a health status label with a segmented progress bar
 * Matches Figma design: https://www.figma.com/design/j1q6kG0YaKkt7ASWZLrvE9/?node-id=16234-2466
 */
const StatusWithBar: React.FC<StatusWithBarProps> = ({
  status,
  filledSegments,
  totalSegments = 10,
  hideLabel = false,
  className = "",
}) => {
  // Normalize legacy status values and ensure valid status
  const normalizedStatus = normalizeStatus(status || "Stable");
  const config = statusConfig[normalizedStatus];

  if (!config) {
    return null;
  }

  const segments = Array.from(
    { length: totalSegments },
    (_, i) => i < filledSegments,
  );

  // Loading state - spinner icon with text
  if (normalizedStatus === "Loading") {
    return (
      <div className={`${styles.statusWithBar} ${className}`}>
        <span className={styles.loadingIconWrapper}>{config.icon}</span>
        {!hideLabel && (
          <div className={styles.statusBadge}>
            <span className={styles.statusText}>{config.label}</span>
          </div>
        )}
      </div>
    );
  }

  // Error state - icon with bold red text, no progress bar
  if (normalizedStatus === "Error") {
    return (
      <div className={`${styles.statusWithBar} ${className}`}>
        {!hideLabel && (
          <div className={styles.statusBadge}>
            <span className={styles.iconWrapper}>{config.icon}</span>
            <span className={styles.errorText}>{config.label}</span>
          </div>
        )}
      </div>
    );
  }

  // Healthy, Stable, Critical states - icon with text and progress bar
  return (
    <div className={`${styles.statusWithBar} ${className}`}>
      {!hideLabel && (
        <div className={styles.statusBadge}>
          <span className={styles.iconWrapper}>{config.icon}</span>
          <span className={styles.statusText}>{config.label}</span>
        </div>
      )}
      <div className={styles.segmentedBar}>
        {segments.map((isFilled, index) => (
          <div
            key={`segment-${normalizedStatus}-${index}`}
            className={`${styles.segment} ${isFilled ? styles.segmentFilled : styles.segmentEmpty}`}
            style={{
              backgroundColor: isFilled ? config.color : "#d4d4d8",
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default StatusWithBar;
